import React, { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout/legacy";
import { Global } from "../helpers/Global";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

export const GestionNodoSIL = () => {
  // =========================
  // Tamaño fijo tarjetas
  // =========================
  const W_FIJO = 1;
  const H_FIJO = 2;

  // =========================
  // State
  // =========================
  const [loading, setLoading] = useState(true);

  // Error “genérico” (rojo arriba)
  const [error, setError] = useState("");

  // 🆕 Error específico del lienzo (cuando backend devuelve 409)
  const [errorLienzo, setErrorLienzo] = useState(null);
  // forma esperada:
  // { message, detalle: { nodo_id, requerido_min_cols, requerido_min_filas } }

  const [nodos, setNodos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [layoutDirty, setLayoutDirty] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);

  const [configSIL, setConfigSIL] = useState({
    cols_canvas: 120,
    filas_canvas: 40,
    col_px: 50,
    row_height: 60,
  });

  // Modales
  const [modalCrear, setModalCrear] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalAsignar, setModalAsignar] = useState(false);

  const [nodoSeleccionado, setNodoSeleccionado] = useState(null);

  // Form
  const [formTitulo, setFormTitulo] = useState("");
  const [formEtiqueta, setFormEtiqueta] = useState("");

  // Asignar
  const [usuarioAsignarId, setUsuarioAsignarId] = useState("");

  // Refs (scroll horizontal arriba + contenedor real)
  const tableroRef = useRef(null);
  const scrollTopRef = useRef(null);
  const syncingScrollRef = useRef(false);

  // =========================
  // Derivados (config lienzo)
  // =========================
  const COLS = Math.max(1, Number(configSIL.cols_canvas || 120));
  const FILAS = Math.max(1, Number(configSIL.filas_canvas || 40));
  const COL_PX = Math.max(20, Number(configSIL.col_px || 50));
  const ROW_HEIGHT = Math.max(20, Number(configSIL.row_height || 60));

  const GRID_WIDTH = COLS * COL_PX;
  const GRID_HEIGHT = FILAS * ROW_HEIGHT;

  // =========================
  // Helpers
  // =========================
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const setErr = (msg) => setError(msg || "");

  /**
   * ✅ NUEVO MODELO SIN ZONAS:
   * - Un nodo está "en panel" si existe fila en sil_layout (x/y no son null/undefined)
   * - Si no hay fila de layout, x/y vienen null => "banco"
   *
   * IMPORTANTÍSIMO:
   * - NO forzamos x/y a 0 cuando vienen null, porque eso lo metería en el panel.
   */
  const normalizarNodo = (n) => {
    const hasLayout =
      n.x !== null &&
      n.x !== undefined &&
      n.y !== null &&
      n.y !== undefined;

    return {
      ...n,
      id: Number(n.id),
      titulo: n.titulo || "",
      etiqueta: n.etiqueta || "",
      // conservar null si no tiene layout
      x: hasLayout ? Number(n.x) : null,
      y: hasLayout ? Number(n.y) : null,
      w: W_FIJO,
      h: H_FIJO,
      enPanel: hasLayout,
    };
  };

  const nodosPrincipal = useMemo(
    () => (nodos || []).filter((n) => !!n.enPanel),
    [nodos]
  );

  const nodosBanco = useMemo(
    () => (nodos || []).filter((n) => !n.enPanel),
    [nodos]
  );

  const layoutPrincipal = useMemo(() => {
    return nodosPrincipal.map((n) => ({
      i: String(n.id),
      x: Number.isFinite(Number(n.x)) ? Number(n.x) : 0,
      y: Number.isFinite(Number(n.y)) ? Number(n.y) : 0,
      w: W_FIJO,
      h: H_FIJO,
    }));
  }, [nodosPrincipal]);

  // =========================
  // Fetch inicial
  // =========================
  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      setErrorLienzo(null);
      try {
        await Promise.all([fetchConfigSIL(), fetchUsuarios(), fetchOrganigrama()]);
      } catch (e) {
        console.error(e);
        setErr("No se pudieron cargar datos.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const fetchOrganigrama = async () => {
    try {
      const res = await fetch(`${Global.url}nodos/organigrama`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        setErr(`Error al obtener organigrama (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      const lista = Array.isArray(data) ? data : data?.data || data?.nodos || [];
      setNodos((lista || []).map(normalizarNodo));
      setLayoutDirty(false);
    } catch (e) {
      console.error("fetchOrganigrama:", e);
      setErr("Error de red al obtener organigrama");
    }
  };

  const fetchUsuarios = async () => {
    try {
      const res = await fetch(`${Global.url}usuarios/todos`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("fetchUsuarios:", e);
    }
  };

  const fetchConfigSIL = async () => {
    try {
      const res = await fetch(`${Global.url}nodos/config`, {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.cols_canvas != null) {
        setConfigSIL({
          cols_canvas: Number(data.cols_canvas || 120),
          filas_canvas: Number(data.filas_canvas || 40),
          col_px: Number(data.col_px || 50),
          row_height: Number(data.row_height || 60),
        });
        setConfigDirty(false);
      }
    } catch (e) {
      console.error("fetchConfigSIL:", e);
    }
  };

  // =========================
  // 🆕 Guardar config con 409
  // =========================
  const guardarConfigSIL = async () => {
    setErr("");
    setErrorLienzo(null);

    try {
      const res = await fetch(`${Global.url}nodos/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(configSIL),
      });

      const body = await res.json().catch(() => ({}));

      // ✅ 409 si rompe layout
      if (res.status === 409) {
        setErrorLienzo({
          message:
            body?.message ||
            "El nuevo tamaño del lienzo no permite el layout actual",
          detalle: body?.detalle || null,
        });
        return;
      }

      if (!res.ok) {
        setErr(body?.message || "Error guardando configuración del lienzo");
        return;
      }

      setConfigDirty(false);
      await fetchConfigSIL();
      alert("Configuración del lienzo guardada");
    } catch (e) {
      console.error(e);
      setErr("Error de red guardando configuración del lienzo");
    }
  };

  // 🆕 Ajustar automáticamente a tamaño mínimo válido
  const ajustarLienzoAMinimoValido = () => {
    if (!errorLienzo?.detalle) return;
    const minCols = Number(errorLienzo.detalle.requerido_min_cols || 0);
    const minFilas = Number(errorLienzo.detalle.requerido_min_filas || 0);

    setConfigSIL((p) => ({
      ...p,
      cols_canvas: Math.max(Number(p.cols_canvas || 1), minCols),
      filas_canvas: Math.max(Number(p.filas_canvas || 1), minFilas),
    }));
    setConfigDirty(true);
    setErrorLienzo(null);
  };

  // =========================
  // Layout: drag
  // =========================
  const onLayoutChange = (newLayout) => {
    setNodos((prev) => {
      const map = new Map((prev || []).map((n) => [String(n.id), { ...n }]));
      (newLayout || []).forEach((it) => {
        const id = String(it.i);
        const n = map.get(id);
        if (!n) return;
        // solo nodos en panel
        if (!n.enPanel) return;

        n.x = Number(it.x) || 0;
        n.y = Number(it.y) || 0;
        n.w = W_FIJO;
        n.h = H_FIJO;

        map.set(id, n);
      });
      return Array.from(map.values());
    });
    setLayoutDirty(true);
  };

  const guardarLayout = async () => {
    setErr("");
    try {
      // ✅ Sin zona: el panel = nodos con layout
      const items = nodosPrincipal.map((n) => ({
        nodoId: n.id,
        x: Number(n.x) || 0,
        y: Number(n.y) || 0,
        w: W_FIJO,
        h: H_FIJO,
      }));

      const res = await fetch(`${Global.url}nodos/layout/guardar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ items }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error guardando layout (HTTP ${res.status})`);
        return;
      }

      setLayoutDirty(false);
      alert("Layout guardado");
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red guardando layout");
    }
  };

  const quitarDelPanel = async (id) => {
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${id}/layout`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(
          body?.message || `Error quitando del panel (HTTP ${res.status})`
        );
        return;
      }
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red quitando del panel");
    }
  };

  /**
   * ✅ IMPORTANTE:
   * ponerEnPanel ahora debe crear layout (fila en sil_layout).
   * No sirve PUT /nodos/:id (ese endpoint solo edita sil_nodos).
   *
   * Necesitas: PUT /nodos/:id/layout
   */
  const ponerEnPanel = async (nodo) => {
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/layout/guardar`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          items: [{ nodoId: nodo.id, x: 0, y: 0, w: W_FIJO, h: H_FIJO }],
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error poniendo en panel (HTTP ${res.status})`);
        return;
      }

      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red poniendo en panel");
    }
  };


  // =========================
  // Scroll horizontal arriba (sync)
  // =========================
  const onScrollTopBar = (e) => {
    if (!tableroRef.current) return;
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    tableroRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  const onScrollTablero = (e) => {
    if (!scrollTopRef.current) return;
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    scrollTopRef.current.scrollLeft = e.target.scrollLeft;
    requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  };

  // =========================
  // CRUD nodo
  // =========================
  const abrirCrearNodo = () => {
    setFormTitulo("");
    setFormEtiqueta("");
    setModalCrear(true);
  };

  const crearNodo = async () => {
    setErr("");
    if (!formTitulo.trim()) {
      setErr("El título es obligatorio");
      return;
    }
    try {
      const res = await fetch(`${Global.url}nodos/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          titulo: formTitulo.trim(),
          // esto va a sil_nodos_usuarios cuando asignas, pero lo mantenías en UI
          // si tu backend no lo usa al crear, no pasa nada enviarlo como null
          etiqueta: formEtiqueta.trim() || null,
          // ✅ ya no enviamos "zona"
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error creando nodo (HTTP ${res.status})`);
        return;
      }
      setModalCrear(false);
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red creando nodo");
    }
  };

  const abrirEditarNodo = (nodo) => {
    setNodoSeleccionado(nodo);
    setFormTitulo(nodo.titulo || "");
    setFormEtiqueta(nodo.etiqueta || "");
    setModalEditar(true);
  };

  const editarNodo = async () => {
    if (!nodoSeleccionado?.id) return;
    setErr("");
    if (!formTitulo.trim()) {
      setErr("El título es obligatorio");
      return;
    }
    try {
      const res = await fetch(`${Global.url}nodos/${nodoSeleccionado.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          titulo: formTitulo.trim(),
          etiqueta: formEtiqueta.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error editando nodo (HTTP ${res.status})`);
        return;
      }
      setModalEditar(false);
      setNodoSeleccionado(null);
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red editando nodo");
    }
  };

  const eliminarNodo = async (id) => {
    if (!window.confirm("¿Eliminar este nodo?")) return;
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error eliminando nodo (HTTP ${res.status})`);
        return;
      }
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red eliminando nodo");
    }
  };

  // =========================
  // Asignación usuario
  // =========================
  const abrirAsignarUsuario = (nodo) => {
    setNodoSeleccionado(nodo);
    setUsuarioAsignarId(nodo?.usuario_id ? String(nodo.usuario_id) : "");
    setModalAsignar(true);
  };

  const asignarUsuario = async () => {
    if (!nodoSeleccionado?.id) return;
    setErr("");
    try {
      const res = await fetch(
        `${Global.url}nodos/${nodoSeleccionado.id}/asignar-usuario`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            usuario_id: usuarioAsignarId ? Number(usuarioAsignarId) : null,
            // etiqueta en tu backend se soporta, pero aquí no estabas enviando; lo dejamos igual
          }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error asignando usuario (HTTP ${res.status})`);
        return;
      }
      setModalAsignar(false);
      setNodoSeleccionado(null);
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red asignando usuario");
    }
  };

  const quitarUsuario = async (nodo) => {
    if (!window.confirm("¿Quitar usuario de este nodo?")) return;
    setErr("");
    try {
      const res = await fetch(`${Global.url}nodos/${nodo.id}/quitar-usuario`, {
        method: "PUT",
        headers: authHeaders(),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.message || `Error quitando usuario (HTTP ${res.status})`);
        return;
      }
      await fetchOrganigrama();
    } catch (e) {
      console.error(e);
      setErr("Error de red quitando usuario");
    }
  };

  // =========================
  // Render tarjeta
  // =========================
  const renderNodoCard = (nodo) => {
    const tieneUsuario = !!nodo?.usuario_id;
    const nombreUsuario = tieneUsuario
      ? `${nodo.nombre || ""} ${nodo.apellidos || ""}`.trim()
      : "Sin asignar";

    return (
      <div className="sil-nodo-card-inner">
        <div className="sil-nodo-titulo">{nodo.titulo}</div>

        <div className={`sil-nodo-usuario ${tieneUsuario ? "" : "muted"}`}>
          {nombreUsuario}
          {nodo.etiqueta ? ` ${nodo.etiqueta}` : ""}
        </div>

        <div className="sil-nodo-acciones">
          <button className="btn-mapa" onClick={() => abrirEditarNodo(nodo)}>
            Editar
          </button>
          <button className="btn-mapa" onClick={() => abrirAsignarUsuario(nodo)}>
            Asignar
          </button>
          <button className="btn-mapa" onClick={() => quitarDelPanel(nodo.id)}>
            Quitar
          </button>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ padding: 20 }}>Cargando...</div>;

  return (
    <div className="sil-gestion-nodos" style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 10 }}>SIL Control · Editor de Nodos (v2)</h2>

      <div className="sil-toolbar" style={{ marginBottom: 10 }}>
        <button className="btn-mapa" onClick={abrirCrearNodo}>
          ➕ Crear nodo
        </button>

        <button className="btn-mapa" onClick={guardarLayout}>
          💾 Guardar layout{layoutDirty ? " *" : ""}
        </button>

        <button className="btn-mapa" onClick={fetchOrganigrama}>
          🔄 Recargar
        </button>

        <div className="sil-canvas-controls">
          <span className="sil-canvas-title">Lienzo</span>

          <label>
            Columnas
            <input
              type="number"
              min="1"
              max="1000"
              value={configSIL.cols_canvas}
              onChange={(e) => {
                setConfigSIL((p) => ({
                  ...p,
                  cols_canvas: Number(e.target.value || 1),
                }));
                setConfigDirty(true);
              }}
            />
          </label>

          <label>
            Filas
            <input
              type="number"
              min="1"
              max="1000"
              value={configSIL.filas_canvas}
              onChange={(e) => {
                setConfigSIL((p) => ({
                  ...p,
                  filas_canvas: Number(e.target.value || 1),
                }));
                setConfigDirty(true);
              }}
            />
          </label>

          <label>
            Ancho (px)
            <input
              type="number"
              min="20"
              max="2000"
              value={configSIL.col_px}
              onChange={(e) => {
                setConfigSIL((p) => ({
                  ...p,
                  col_px: Number(e.target.value || 50),
                }));
                setConfigDirty(true);
              }}
            />
          </label>

          <label>
            Alto fila
            <input
              type="number"
              min="20"
              max="400"
              value={configSIL.row_height}
              onChange={(e) => {
                setConfigSIL((p) => ({
                  ...p,
                  row_height: Number(e.target.value || 60),
                }));
                setConfigDirty(true);
              }}
            />
          </label>

          <button type="button" className="btn-mapa" onClick={guardarConfigSIL}>
            ⚙️ Guardar lienzo{configDirty ? " *" : ""}
          </button>
        </div>

        {error && (
          <span className="rojo" style={{ marginLeft: 10 }}>
            {error}
          </span>
        )}
      </div>

      {/* 🆕 Caja de error específica del lienzo (409) */}
      {errorLienzo && (
        <div
          style={{
            marginBottom: 14,
            padding: 12,
            borderRadius: 10,
            border: "1px solid rgba(192,57,43,.35)",
            background: "rgba(192,57,43,.08)",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ❌ {errorLienzo.message}
          </div>

          {errorLienzo.detalle ? (
            <div style={{ fontSize: 13 }}>
              El nodo <b>#{errorLienzo.detalle.nodo_id}</b> necesita al menos{" "}
              <b>{errorLienzo.detalle.requerido_min_cols}</b> columnas y{" "}
              <b>{errorLienzo.detalle.requerido_min_filas}</b> filas para no
              salirse.
            </div>
          ) : (
            <div style={{ fontSize: 13 }}>
              Reduce menos el lienzo o reubica los nodos antes de guardar.
            </div>
          )}

          {errorLienzo.detalle && (
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn-mapa" onClick={ajustarLienzoAMinimoValido}>
                Ajustar al mínimo válido
              </button>
              <button className="btn-mapa" onClick={() => setErrorLienzo(null)}>
                Cerrar
              </button>
            </div>
          )}
        </div>
      )}

      {/* ✅ BANCO IZQUIERDA + PANEL DERECHA */}
      <div className="sil-zonas" style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        {/* BANCO IZQUIERDA */}
        <div style={{ width: 340 }}>
          <h3 style={{ marginTop: 10 }}>Banco (sin colocar)</h3>

          {nodosBanco.length === 0 ? (
            <p className="muted">No hay nodos en el banco.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {nodosBanco.map((n) => {
                const tieneUsuario = !!n?.usuario_id;
                const nombreUsuario = tieneUsuario
                  ? `${n.nombre || ""} ${n.apellidos || ""}`.trim()
                  : "Sin asignar";

                return (
                  <div key={n.id} className="sil-nodo-card">
                    <div className="sil-nodo-card-inner" style={{ gap: 6 }}>
                      <div className="sil-nodo-titulo">{n.titulo}</div>
                      <div className={`sil-nodo-usuario ${tieneUsuario ? "" : "muted"}`}>
                        {nombreUsuario}
                        {n.etiqueta ? ` ${n.etiqueta}` : ""}
                      </div>

                      <div className="sil-nodo-acciones" style={{ flexWrap: "wrap" }}>
                        <button className="btn-mapa" onClick={() => abrirEditarNodo(n)}>
                          Editar
                        </button>
                        <button className="btn-mapa" onClick={() => abrirAsignarUsuario(n)}>
                          Asignar
                        </button>

                        {/* ✅ ahora crea layout */}
                        <button className="btn-mapa" onClick={() => ponerEnPanel(n)}>
                          Poner en panel
                        </button>

                        <button className="btn-mapa" onClick={() => eliminarNodo(n.id)}>
                          Eliminar
                        </button>

                        {tieneUsuario && (
                          <button className="btn-mapa" onClick={() => quitarUsuario(n)}>
                            Quitar usuario
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* PANEL PRINCIPAL DERECHA */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ marginTop: 10 }}>Zona principal</h3>

          {/* ✅ Scroll horizontal superior */}
          <div
            ref={scrollTopRef}
            onScroll={onScrollTopBar}
            style={{
              height: 16,
              overflowX: "auto",
              overflowY: "hidden",
              marginBottom: 6,
              borderRadius: 10,
            }}
          >
            <div style={{ width: GRID_WIDTH, height: 1 }} />
          </div>

          <div
            ref={tableroRef}
            className="sil-grid-contenedor sil-grid-contenedor--tablero"
            onScroll={onScrollTablero}
            style={{
              height: "calc(100vh - 280px)",
              overflow: "auto",
              border: "1px solid rgba(0,0,0,.06)",
              borderRadius: 10,
              background: "#fff",
            }}
          >
            <div style={{ width: GRID_WIDTH, height: GRID_HEIGHT }}>
              <GridLayout
                layout={layoutPrincipal}
                cols={COLS}
                rowHeight={ROW_HEIGHT}
                width={GRID_WIDTH}
                maxRows={FILAS}
                margin={[12, 12]}
                containerPadding={[12, 12]}
                isResizable={false}
                isDraggable={true}
                preventCollision={false}
                compactType={null}
                onLayoutChange={onLayoutChange}
              >
                {nodosPrincipal.map((n) => (
                  <div key={String(n.id)} className="sil-nodo-card">
                    {renderNodoCard(n)}
                  </div>
                ))}
              </GridLayout>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL CREAR */}
      {modalCrear && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <button className="modal-close" onClick={() => setModalCrear(false)}>
              &times;
            </button>
            <h3>Crear nodo</h3>

            <div className="form-group">
              <label>Título *</label>
              <input value={formTitulo} onChange={(e) => setFormTitulo(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Etiqueta (opcional)</label>
              <input value={formEtiqueta} onChange={(e) => setFormEtiqueta(e.target.value)} />
            </div>

            {error && <p className="rojo">{error}</p>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn-mapa" onClick={() => setModalCrear(false)}>
                Cancelar
              </button>
              <button className="btn-mapa" onClick={crearNodo}>
                Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EDITAR */}
      {modalEditar && nodoSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <button className="modal-close" onClick={() => setModalEditar(false)}>
              &times;
            </button>
            <h3>Editar nodo</h3>

            <div className="form-group">
              <label>Título *</label>
              <input value={formTitulo} onChange={(e) => setFormTitulo(e.target.value)} />
            </div>

            <div className="form-group">
              <label>Etiqueta (opcional)</label>
              <input value={formEtiqueta} onChange={(e) => setFormEtiqueta(e.target.value)} />
            </div>

            {error && <p className="rojo">{error}</p>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn-mapa" onClick={() => setModalEditar(false)}>
                Cancelar
              </button>
              <button className="btn-mapa" onClick={editarNodo}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ASIGNAR */}
      {modalAsignar && nodoSeleccionado && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 560 }}>
            <button className="modal-close" onClick={() => setModalAsignar(false)}>
              &times;
            </button>
            <h3>Asignar usuario a: {nodoSeleccionado.titulo}</h3>

            <div className="form-group">
              <label>Usuario</label>
              <select value={usuarioAsignarId} onChange={(e) => setUsuarioAsignarId(e.target.value)}>
                <option value="">Sin asignar</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {`${u.nombre || ""} ${u.apellidos || ""}`.trim()} ({u.email || u.dni || "sin id"})
                  </option>
                ))}
              </select>
            </div>

            {error && <p className="rojo">{error}</p>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn-mapa" onClick={() => setModalAsignar(false)}>
                Cancelar
              </button>
              <button className="btn-mapa" onClick={asignarUsuario}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
